// src/layouts/Pages/PageHeader/RemoveButton.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React from 'react';
import { Button, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { useModuleContext } from 'components/core/ModuleProvider';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RemoveButton = ({
  actions = [],
  actionContext,
  label,
  tooltip,
  icon,
  type,
  disabled,
  onClick,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const moduleContext = useModuleContext();
  const { handleRemoveDocuments } = moduleContext;

  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const hasAction = key =>
    actions.includes(key) ||
    actions.some(a => typeof a === 'object' && a?.action === key);

  const showRemoveButton = hasAction('remove');

  // Profile-based visibility: only owner and adm can see and use remove
  const profile = storedSubscription?.profile;
  const canRemove = profile === 'owner' || profile === 'adm';

  // Disabled when: no profile permission, or document not yet saved (no _id)
  const resolvedDisabled =
    typeof disabled === 'function'
      ? disabled({ page: storedCurrentPage })
      : disabled;

  const hasDocument = !!storedCurrentPage?.data?._id;
  const computedDisabled = resolvedDisabled ?? !hasDocument;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClick = () => {
    if (typeof onClick === 'function') {
      return onClick({ handleRemoveDocuments, page: storedCurrentPage });
    }
    const data = storedCurrentPage?.data;
    if (!data?._id) return;
    handleRemoveDocuments?.([data], {});
  };

  // Prevent blur from stealing focus before click registers
  const handleMouseDown = e => {
    e.preventDefault();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const buttonIcon = icon || <DeleteOutlined />;
  const buttonType = type;
  const title = translateX(tooltip || label || 'remove');

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // Hidden for non-owner/non-adm profiles and when action is not declared
  if (!showRemoveButton || !canRemove) return null;

  return (
    <Tooltip title={title} placement="top">
      <Button
        type={buttonType}
        danger={!computedDisabled}
        disabled={computedDisabled}
        icon={buttonIcon}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
      />
    </Tooltip>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RemoveButton;
